export const ZoomInNavbar = {
  // FA204 / frank trap — getGS/getIframeWindow used to be bare module-scope
  // functions read from inside `onFrame`/`onClick`/`onDblclick` factories;
  // they read as undefined once this component is frank.toJSON'd for
  // platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called
  // via `el.scope.<name>`.
  scope: {
    getGS: () => window.__canvasGlobalScope,
    getIframeWindow: () => {
      const iframe = document.getElementById('canvas-iframe')
      return iframe?.contentWindow || window
    },
    // ZOOMBAR-PRESET-DROPDOWN-DEAD-1 — the presets behind the arrow
    // (central decision, option A: 50/75/100/150/200% + fit). `fit` has no
    // scale of its own: it routes to the canvas's existing fit helper
    // (`fitToSelection` — fits the selection, or everything when nothing is
    // selected; the same helper ⌘1 and the `/fit` command use). Only the v2
    // project canvas registers that helper's data (the `__v2` panzoom shim),
    // so the Fit row is enabled per-frame below only on that surface — the
    // studios / tickets / infra / team boards mirror a real panzoom instance
    // and keep their own module-private fit, so Fit would be a silent no-op
    // there (a fake affordance — the very thing this ticket exists to
    // remove). Lives in `scope`, not module scope (FA204 frank trap).
    ZOOM_PRESETS: [
      { key: 'p50', label: '50%', scale: 0.5 },
      { key: 'p75', label: '75%', scale: 0.75 },
      { key: 'p100', label: '100%', scale: 1 },
      { key: 'p150', label: '150%', scale: 1.5 },
      { key: 'p200', label: '200%', scale: 2 },
      { key: 'fit', label: 'editor.zoombar.fitToContent', fit: true }
    ],
    // `this` is the scope bag (el.scope.<name>() binds it). Returns true
    // when a zoom was issued, false when panzoom is not ready — the rows are
    // disabled then anyway, this is the same defense-in-depth the +/-
    // buttons carry.
    applyZoomPreset(el, preset) {
      const gs = this.getGS()
      if (!gs?.panzoomInstance || !preset) return false
      if (preset.fit) {
        // A canvas registered through registerCanvasPanzoom (a module canvas page) brings its own fit on the instance.
        if (typeof gs.panzoomInstance.__fit === 'function') {
          gs.panzoomInstance.__fit()
          return true
        }
        if (!gs.panzoomInstance.__v2) return false
        el.call('fitToSelection')
        return true
      }
      const win = this.getIframeWindow()
      const { x, y } = gs.positions || { x: 0, y: 0 }
      gs.panzoomInstance.smoothZoomAbs(
        x + win.innerWidth / 2,
        y + win.innerHeight / 2,
        preset.scale
      )
      return true
    }
  },
  // INVISIBLE-OVERLAY-EATS-CLICKS-1 #3: before this, the buttons stayed
  // enabled-looking through the ~20s the global scope takes to register on
  // a fresh boot, and each onClick silently `return`ed with zero feedback
  // (a dead app is indistinguishable from a slow one).
  //
  // ⚠️ FIRST ATTEMPT used a reactive `state.panzoomReady` + `disabled: (el,
  // s) => !s.panzoomReady` on the buttons — live-verified BROKEN (real
  // click, not synthetic dispatch: `el.update({panzoomReady:true})` fired
  // correctly from onFrame, but the child buttons' `disabled:` factory
  // never re-evaluated — both buttons stayed permanently `disabled=""`,
  // including once panzoom was genuinely ready. Reactive-prop dependency
  // tracking does not pick this one up; do not reintroduce it without
  // re-verifying live first.
  //
  // Fixed the SAME way the file already handles the % label two lines
  // down, for the same reason that comment gives (a reactive re-render
  // here has already caused a real regression once): grab the real button
  // DOM nodes from the SAME already-running per-frame loop and set
  // `.disabled`/`.title` as plain DOM property writes, which are governed
  // by the HTML spec, not this component's own reactivity. Only touched on
  // an actual readiness transition, both directions — a route switch
  // between canvas surfaces can tear one panzoom instance down before the
  // next registers, and Zoombar is one shell-root mount serving every
  // surface, so re-arming on the way down is what makes the SECOND
  // surface's boot correct too, not just the first. The % label eager-syncs
  // the moment readiness flips instead of waiting for the next scale
  // change (the bonus defect: a scale that changed via wheel-zoom, which
  // is NOT gated by these buttons, while the buttons were still dead left
  // the label frozen at its mount-time default, e.g. "100%" at a real
  // 52.5%).
  flow: 'x',
  onFrame: (el) => {
    const pz = el.scope.getGS()?.panzoomInstance
    const ready = !!pz
    if (ready !== el._lastPanzoomReady) {
      el._lastPanzoomReady = ready
      const outNode = el.node && el.node.querySelector(':scope > [data-key="NavbarButton_out"]')
      const inNode = el.node && el.node.querySelector(':scope > [data-key="NavbarButton_in"]')
      const presetsNode =
        el.node && el.node.querySelector(':scope > [data-key="NavbarButton_presets"]')
      const title = ready ? '' : el.call('polyglot', 'editor.zoombar.loading')
      // The cursor is deliberately NOT written here.
      // WS-ZOOMBAR-DISABLED-LOOKS-ENABLED-1 had to set `node.style.cursor`
      // imperatively, because FocusableComponent shipped `cursor: pointer`
      // inside its `style: {}` escape hatch — an INLINE declaration that beats
      // any `:disabled` stylesheet rule regardless of specificity. smbls
      // fc681ac1d moved that cursor onto the atomic engine (flat
      // `cursor: 'pointer'` plus `':disabled': { cursor: 'not-allowed' }`), so
      // the `:disabled` block each button declares below now wins on its own
      // and the imperative write became dead code
      // (WS-ZOOMINNAVBAR-IMPERATIVE-CURSOR-WORKAROUND-IS-DEAD-CODE-1).
      // Published in @symbo.ls/default-config 3.14.782; this repo resolves
      // 3.14.783. Never restore the write on a DOWNGRADE below 3.14.782 — fix
      // the dep. `packages/workspace/__tests__/zoombarCursorRidesTheAtom.test.js`
      // goes red on exactly that.
      if (outNode) {
        outNode.disabled = !ready
        outNode.title = title || 'Zoom out'
      }
      if (inNode) {
        inNode.disabled = !ready
        inNode.title = title || 'Zoom in'
      }
      if (presetsNode) {
        presetsNode.disabled = !ready
        presetsNode.title = title || 'Zoom presets'
      }
    }
    // Fit row readiness is a SECOND transition: it follows which surface
    // owns the panzoom (see scope.ZOOM_PRESETS), not just whether one exists.
    const fitReady = !!(pz && (pz.__v2 || typeof pz.__fit === 'function'))
    if (fitReady !== el._lastFitReady) {
      el._lastFitReady = fitReady
      const fitNode = el.node && el.node.querySelector('[data-key="MenuPanelItem_fit"]')
      if (fitNode) {
        fitNode.disabled = !fitReady
        fitNode.title = fitReady ? '' : el.call('polyglot', 'editor.zoombar.fitUnavailable')
      }
    }
    const scale = pz ? pz.getTransform().scale : 0.35
    const text = parseInt(scale * 100) + '%'
    // Update the percent label by targeting the Box's OWN node via data-key.
    // `el.Box.node` resolved to ZoomInNavbar's node in v3.14, so the previous
    // `el.Box.node.innerText = text` wiped the zoom +/- buttons + dropdown
    // arrow on every frame — collapsing the whole control to a bare "NN%"
    // (the broken layout). Setting `textContent` on the child node updates
    // only the label and leaves the buttons intact.
    const boxNode = el.node && el.node.querySelector(':scope > [data-key="Box"]')
    if (boxNode && boxNode.textContent !== text) boxNode.textContent = text
  },
  align: 'center',
  NavbarButton_out: {
    // Static defaults matching onFrame's own not-ready branch — real state
    // corrected imperatively above the instant readiness is known, same
    // pattern as the % label's own '100%' static default just below.
    disabled: true,
    title: (el) => el.call('polyglot', 'editor.zoombar.loading'),
    icon: 'sf zoom out',
    // WS-ZOOMBAR-DISABLED-LOOKS-ENABLED-1 — native :disabled tracks the
    // plain `.disabled` DOM writes in onFrame above for free. Canonical
    // disabled treatment (brand-language skill): dimmed ink + opacity +
    // non-pointer cursor, never color-only — same shape as CheckButton /
    // AppField / PersonaPill's ExitBtn.
    ':disabled': {
      color: 'disabled',
      opacity: '.55',
      cursor: 'not-allowed'
    },
    // `:not(:disabled)` added — plain `:not(:hover)` tied on specificity
    // with the `:disabled` block above (both class+1-pseudo) and, being
    // later in source order, won the `color` property while disabled.
    // Mutually excluding `:disabled` here makes the two states disjoint
    // instead of racing: the idle transparent-theme treatment is an
    // enabled-state affordance anyway.
    ':not(:hover):not(:disabled)': {
      theme: 'transparent'
    },
    onClick: (ev, el) => {
      const gs = el.scope.getGS()
      // Defense in depth — a compliant browser never fires onClick on a
      // disabled control, but the guard costs nothing and this is exactly
      // the class of "handler returns early" the ticket exists to catch.
      if (!gs?.panzoomInstance) return
      const win = el.scope.getIframeWindow()
      const { x, y } = gs.positions
      gs.panzoomInstance.smoothZoom(x + win.innerWidth / 2, y + win.innerHeight / 2, 0.75)
    }
  },
  Box: {
    width: '7ch',
    textAlign: 'center',
    padding: '- Z',
    fontSize: 'Z2',
    color: 'paragraph',
    text: '100%',
    userSelect: 'none',
    onDblclick: (ev, el) => {
      const gs = el.scope.getGS()
      if (!gs?.panzoomInstance) return
      const win = el.scope.getIframeWindow()
      const { x, y } = gs.positions
      gs.panzoomInstance.smoothZoomAbs(x + win.innerWidth / 2, y + win.innerHeight / 2, 1)
    }
  },
  NavbarButton_in: {
    disabled: true,
    title: (el) => el.call('polyglot', 'editor.zoombar.loading'),
    icon: 'sf zoom in',
    ':disabled': {
      color: 'disabled',
      opacity: '.55',
      cursor: 'not-allowed'
    },
    ':not(:hover):not(:disabled)': {
      theme: 'transparent'
    },
    onClick: (ev, el) => {
      const gs = el.scope.getGS()
      if (!gs?.panzoomInstance) return
      const win = el.scope.getIframeWindow()
      const { x, y } = gs.positions
      gs.panzoomInstance.smoothZoom(x + win.innerWidth / 2, y + win.innerHeight / 2, 1.25)
    }
  },
  // ZOOMBAR-PRESET-DROPDOWN-DEAD-1 — the arrow used to be a bare
  // `DropdownArrow: { margin: '- - - X2' }` here: an Icon with no sibling
  // Dropdown, no onClick, not a button — a fake affordance. It is now the
  // third NavbarButton of the stepper (same circle, same transparent idle,
  // same disabled-until-ready mechanism as +/- above) carrying the house
  // DropdownParentFocus idiom the sibling navbars use (EnvsNavbar,
  // ModesInNavbar, CanvasPagesDropdown): focus-within opens the child
  // Dropdown, blur / outside click / a row click closes it, Escape below.
  //
  // INLINE, NOT THE openDropdown PORTAL, ON PURPOSE: the shell's DropdownRoot
  // (10010) sits under CreditBlocker's gate (10300) by design ("a leftover
  // open menu must not float over the gate"), while this whole bar sits
  // above it (10310 — pan/zoom is a pure read, parent ticket). A child
  // Dropdown shares the bar's stacking context, so the menu is reachable
  // through the gate exactly like the buttons, with no ladder change.
  NavbarButton_presets: {
    // Explicit, not key-derived: a literal `extends` REPLACES the key's
    // implicit NavbarButton (live-verified: `extends: 'DropdownParentFocus'`
    // alone rendered a bare 12px <div>), so both are listed — same shape as
    // ModesInNavbar's `extends: ['CanvasButton', …, 'DropdownParentFocus']`.
    extends: ['NavbarButton', 'DropdownParentFocus'],
    disabled: true,
    title: (el) => el.call('polyglot', 'editor.zoombar.presets'),
    aria: { haspopup: 'menu' },
    ':disabled': {
      color: 'disabled',
      opacity: '.55',
      cursor: 'not-allowed'
    },
    ':not(:hover):not(:disabled)': {
      theme: 'transparent'
    },
    // Escape closes: DropdownParentFocus only closes on blur. The focused
    // node (the trigger or a row) is ev.target — blur() is the one write
    // the DOM contract allows on a node.
    onKeydown: (ev) => {
      if (ev.key !== 'Escape') return
      ev.stopPropagation()
      if (ev.target && typeof ev.target.blur === 'function') ev.target.blur()
    },
    // The idiom's own trigger toggles by comparing `activeElement` AT CLICK
    // time — but Chrome has already moved focus onto this checkbox on
    // mousedown, so a first real click opened and closed the menu in one
    // gesture (live-verified on this node: activeElement === body right after
    // the click). Decide on mousedown instead: already focused means open →
    // keep focus from re-landing and blur (close); otherwise the default
    // focus move opens it. The click itself is then a no-op.
    Input_trigger: {
      onMousedown: (ev, el) => {
        if (!el.node || !el.node.matches(':focus')) return
        ev.preventDefault()
        el.node.blur()
      },
      onClick: () => {}
    },
    DropdownArrow: {},
    // Opens UPWARD and right-aligned: the bar is docked bottom-right.
    Dropdown: {
      bottom: '115%',
      top: 'auto',
      right: '0',
      left: 'auto',
      round: 'radiusCard',
      // The brand Dropdown is the positioned/animated shell; the visible
      // surface is the house MenuPanel chassis (DESIGN_LANGUAGE.md §7.7),
      // same nesting as ModesInNavbar's Dropdown.MoreModesMenu.
      MenuPanel: {
        role: 'menu',
        // The menu lives INSIDE the <button> (Button base: 700) — rows are
        // body weight, not button weight (brand weight law: 400 body).
        fontWeight: '400',
        ariaLabel: (el) => el.call('polyglot', 'editor.zoombar.presets'),
        MenuPanelItem_p50: {
          role: 'menuitem',
          text: '50%',
          onClick: (ev, el) => {
            el.scope.applyZoomPreset(el, el.scope.ZOOM_PRESETS[0])
            el.node?.blur?.()
          }
        },
        MenuPanelItem_p75: {
          role: 'menuitem',
          text: '75%',
          onClick: (ev, el) => {
            el.scope.applyZoomPreset(el, el.scope.ZOOM_PRESETS[1])
            el.node?.blur?.()
          }
        },
        MenuPanelItem_p100: {
          role: 'menuitem',
          text: '100%',
          onClick: (ev, el) => {
            el.scope.applyZoomPreset(el, el.scope.ZOOM_PRESETS[2])
            el.node?.blur?.()
          }
        },
        MenuPanelItem_p150: {
          role: 'menuitem',
          text: '150%',
          onClick: (ev, el) => {
            el.scope.applyZoomPreset(el, el.scope.ZOOM_PRESETS[3])
            el.node?.blur?.()
          }
        },
        MenuPanelItem_p200: {
          role: 'menuitem',
          text: '200%',
          onClick: (ev, el) => {
            el.scope.applyZoomPreset(el, el.scope.ZOOM_PRESETS[4])
            el.node?.blur?.()
          }
        },
        MenuPanelItem_fit: {
          role: 'menuitem',
          text: '{{ editor.zoombar.fitToContent | polyglot }}',
          // Static default, corrected per-frame (see onFrame's fit transition).
          disabled: true,
          title: (el) => el.call('polyglot', 'editor.zoombar.fitUnavailable'),
          // WS-ZOOMBAR-DISABLED-LOOKS-ENABLED-1 — MenuPanelItem's base sets
          // cursor: 'pointer' unconditionally with no :disabled step.
          ':disabled': {
            color: 'disabled',
            opacity: '.55',
            cursor: 'not-allowed'
          },
          onClick: (ev, el) => {
            el.scope.applyZoomPreset(el, el.scope.ZOOM_PRESETS[5])
            el.node?.blur?.()
          }
        }
      }
    }
  }
}
